import { LGraphNode, LiteGraph } from "litegraph.js";
import { addDrivableSlider, finalizeDrivableSliders, syncDrivableSliders } from "../utils/addDrivableSlider.js";

/**
 * PixelSortNode
 * -------------
 * Sorts pixel rows by brightness within a threshold range.
 * Classic glitch-art / datamosh aesthetic.
 */
export class PixelSortNode extends LGraphNode {
  constructor() {
    super();
    this.title = "Pixel Sort";

    this.properties = {
      threshold: 0.3,
      upperThreshold: 0.8,
      direction: "horizontal",
    };

    this.addInput("Canvas", "canvas");
    this.addOutput("Canvas", "canvas");

    this.addWidget("combo", "Direction", "horizontal", (val) => {
      this.properties.direction = val;
    }, { values: ["horizontal", "vertical"] });

    addDrivableSlider(this, "Lower", "threshold", 0.3, 0, 1, 0.01);
    addDrivableSlider(this, "Upper", "upperThreshold", 0.8, 0, 1, 0.01);

    finalizeDrivableSliders(this, 1);

    this._outCanvas = document.createElement("canvas");
    this._outCtx = this._outCanvas.getContext("2d");
    this._tmpCanvas = document.createElement("canvas");
    this._tmpCtx = this._tmpCanvas.getContext("2d");
    this._frameSkip = 0;

    this.size = [200, 110];
    this.mode = LiteGraph.ALWAYS;
    this.color = "#2a2a2a";
    this.bgcolor = "#333333";
  }

  onExecute() {
    syncDrivableSliders(this);

    const input = this.getInputData(0);
    if (!input) { this.setOutputData(0, null); return; }

    // Skip frames for performance (pixel sort is expensive)
    this._frameSkip++;
    if (this._frameSkip % 3 !== 0 && this._outCanvas.width > 0) {
      this.setOutputData(0, this._outCanvas);
      return;
    }

    const w = input.width;
    const h = input.height;
    // Work at reduced resolution for performance
    const scale = Math.min(1, 800 / Math.max(w, h));
    const sw = Math.floor(w * scale);
    const sh = Math.floor(h * scale);

    if (this._outCanvas.width !== w) this._outCanvas.width = w;
    if (this._outCanvas.height !== h) this._outCanvas.height = h;

    // Draw scaled down (reuse temp canvas)
    if (this._tmpCanvas.width !== sw) this._tmpCanvas.width = sw;
    if (this._tmpCanvas.height !== sh) this._tmpCanvas.height = sh;
    this._tmpCtx.clearRect(0, 0, sw, sh);
    this._tmpCtx.drawImage(input, 0, 0, sw, sh);

    const imageData = this._tmpCtx.getImageData(0, 0, sw, sh);
    const data = imageData.data;
    const { threshold, upperThreshold, direction } = this.properties;

    if (direction === "horizontal") {
      for (let y = 0; y < sh; y++) {
        this._sortRow(data, sw, y, threshold, upperThreshold);
      }
    } else {
      for (let x = 0; x < sw; x++) {
        this._sortCol(data, sw, sh, x, threshold, upperThreshold);
      }
    }

    this._tmpCtx.putImageData(imageData, 0, 0);

    // Scale back up
    this._outCtx.clearRect(0, 0, w, h);
    this._outCtx.imageSmoothingEnabled = true;
    this._outCtx.drawImage(this._tmpCanvas, 0, 0, w, h);

    this.setOutputData(0, this._outCanvas);
  }

  _brightness(data, idx) {
    return (data[idx] * 0.299 + data[idx + 1] * 0.587 + data[idx + 2] * 0.114) / 255;
  }

  _sortRow(data, w, y, lo, hi) {
    // Collect pixels in range
    const row = [];
    for (let x = 0; x < w; x++) {
      const idx = (y * w + x) * 4;
      const b = this._brightness(data, idx);
      row.push({ x, b, r: data[idx], g: data[idx + 1], bl: data[idx + 2], a: data[idx + 3] });
    }

    // Find contiguous segments within threshold
    let i = 0;
    while (i < row.length) {
      if (row[i].b >= lo && row[i].b <= hi) {
        let j = i;
        while (j < row.length && row[j].b >= lo && row[j].b <= hi) j++;
        const segment = row.slice(i, j);
        segment.sort((a, b) => a.b - b.b);
        for (let k = 0; k < segment.length; k++) {
          const idx = (y * w + (i + k)) * 4;
          data[idx] = segment[k].r;
          data[idx + 1] = segment[k].g;
          data[idx + 2] = segment[k].bl;
          data[idx + 3] = segment[k].a;
        }
        i = j;
      } else {
        i++;
      }
    }
  }

  _sortCol(data, w, h, x, lo, hi) {
    const col = [];
    for (let y = 0; y < h; y++) {
      const idx = (y * w + x) * 4;
      const b = this._brightness(data, idx);
      col.push({ y, b, r: data[idx], g: data[idx + 1], bl: data[idx + 2], a: data[idx + 3] });
    }

    let i = 0;
    while (i < col.length) {
      if (col[i].b >= lo && col[i].b <= hi) {
        let j = i;
        while (j < col.length && col[j].b >= lo && col[j].b <= hi) j++;
        const segment = col.slice(i, j);
        segment.sort((a, b) => a.b - b.b);
        for (let k = 0; k < segment.length; k++) {
          const idx = ((i + k) * w + x) * 4;
          data[idx] = segment[k].r;
          data[idx + 1] = segment[k].g;
          data[idx + 2] = segment[k].bl;
          data[idx + 3] = segment[k].a;
        }
        i = j;
      } else {
        i++;
      }
    }
  }
}

LiteGraph.registerNodeType("effects/PixelSort", PixelSortNode);
